import { Button, type ButtonProps } from '@mantine/core'
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react'
import {
  IconCheck,
  IconDeviceFloppy,
  IconRefresh,
  IconTrash,
  IconX,
  IconArrowLeft,
  IconPlayerPlay,
  IconPlus,
  IconSearch,
  IconUpload,
  IconDownload,
} from '@/components/icons'

export type ActionButtonPreset =
  | 'save'
  | 'cancel'
  | 'apply'
  | 'refresh'
  | 'accept'
  | 'reject'
  | 'delete'
  | 'add'
  | 'close'
  | 'submit'
  | 'back'
  | 'continue'
  | 'download'
  | 'upload'
  | 'search'
  | 'reset'

const presetConfig: Record<
  ActionButtonPreset,
  { label: string; defaultVariant: ButtonProps['variant']; defaultColor: ButtonProps['color']; icon?: ReactNode }
> = {
  save: {
    label: 'Сохранить',
    defaultVariant: 'filled',
    defaultColor: 'brand',
    icon: <IconDeviceFloppy size={18} stroke={1.5} />,
  },
  cancel: {
    label: 'Отмена',
    defaultVariant: 'default',
    defaultColor: 'gray',
    icon: <IconX size={18} stroke={1.5} />,
  },
  apply: {
    label: 'Применить',
    defaultVariant: 'filled',
    defaultColor: 'brand',
    icon: <IconCheck size={18} stroke={1.5} />,
  },
  refresh: {
    label: 'Обновить',
    defaultVariant: 'default',
    defaultColor: 'gray',
    icon: <IconRefresh size={18} stroke={1.5} />,
  },
  accept: {
    label: 'Принять',
    defaultVariant: 'filled',
    defaultColor: 'green',
    icon: <IconCheck size={18} stroke={1.5} />,
  },
  reject: {
    label: 'Отклонить',
    defaultVariant: 'outline',
    defaultColor: 'red',
    icon: <IconX size={18} stroke={1.5} />,
  },
  delete: {
    label: 'Удалить',
    defaultVariant: 'filled',
    defaultColor: 'red',
    icon: <IconTrash size={18} stroke={1.5} />,
  },
  add: {
    label: 'Добавить',
    defaultVariant: 'filled',
    defaultColor: 'brand',
    icon: <IconPlus size={18} stroke={1.5} />,
  },
  close: {
    label: 'Закрыть',
    defaultVariant: 'default',
    defaultColor: 'gray',
    icon: <IconX size={18} stroke={1.5} />,
  },
  submit: {
    label: 'Отправить',
    defaultVariant: 'filled',
    defaultColor: 'brand',
  },
  back: {
    label: 'Назад',
    defaultVariant: 'subtle',
    defaultColor: 'gray',
    icon: <IconArrowLeft size={18} stroke={1.5} />,
  },
  continue: {
    label: 'Продолжить',
    defaultVariant: 'filled',
    defaultColor: 'brand',
    icon: <IconPlayerPlay size={18} stroke={1.5} />,
  },
  download: {
    label: 'Скачать',
    defaultVariant: 'light',
    defaultColor: 'brand',
    icon: <IconDownload size={18} stroke={1.5} />,
  },
  upload: {
    label: 'Загрузить',
    defaultVariant: 'light',
    defaultColor: 'brand',
    icon: <IconUpload size={18} stroke={1.5} />,
  },
  search: {
    label: 'Найти',
    defaultVariant: 'filled',
    defaultColor: 'brand',
    icon: <IconSearch size={18} stroke={1.5} />,
  },
  reset: {
    label: 'Сбросить',
    defaultVariant: 'subtle',
    defaultColor: 'gray',
    icon: <IconRefresh size={18} stroke={1.5} />,
  },
}

/** Mantine `ButtonProps` в v8 не тянет нативные атрибуты `button` в типах — добавляем вручную */
export type ActionButtonProps = ButtonProps &
  Pick<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'onClick' | 'type' | 'form' | 'name' | 'id' | 'title' | 'tabIndex'
  > & {
    action: ActionButtonPreset
    label?: string
    withIcon?: boolean
  }

/**
 * Кнопки с едиными подписями и иконками: сохранить, отмена, применить, обновить и т.д.
 */
export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  function ActionButton(
    { action, label, withIcon = true, variant, color, leftSection, children, ...props },
    ref,
  ) {
    const cfg = presetConfig[action]
    const text = label ?? cfg.label
    const ls =
      leftSection ??
      (withIcon && cfg.icon ? cfg.icon : undefined)

    return (
      <Button
        ref={ref}
        variant={variant ?? cfg.defaultVariant}
        color={color ?? cfg.defaultColor}
        leftSection={ls}
        {...props}
      >
        {children ?? text}
      </Button>
    )
  },
)
